/**
 * AgentSettingsLink — compact "<Agent> agent settings" link + playbook chip pointing at
 * Settings → AI agents. Shown on the Repairs list and in the Leasing area header; replaces the
 * default-policy switches that used to live on the Repairs page. If the playbook summary cannot
 * be fetched the link still renders, without the chip.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { Badge } from '../ui/badge';
import { getAgentPlaybooks } from '../../api/agent-playbooks.api';
import { agentName, isPlaybookCustomized } from '../../lib/playbook';
import type { PlaybookAgentKind } from '@resida/api-types/agent';

export const AGENT_PLAYBOOKS_QUERY_KEY = ['agent-playbooks'] as const;

export function AgentSettingsLink({ agentKind }: { agentKind: PlaybookAgentKind }) {
  const { data } = useQuery({
    queryKey: AGENT_PLAYBOOKS_QUERY_KEY,
    queryFn: getAgentPlaybooks,
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
  const view = data?.playbooks.find((p) => p.agentKind === agentKind);

  return (
    <Link
      to="/settings/agents"
      data-testid={`agent-settings-link-${agentKind}`}
      className="inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
    >
      <Bot className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{agentName(agentKind)} settings</span>
      {view && (
        <Badge
          variant={isPlaybookCustomized(view) ? 'primary' : 'secondary'}
          data-testid={`agent-settings-link-chip-${agentKind}`}
        >
          {isPlaybookCustomized(view) ? 'Playbook customized' : 'Using defaults'}
        </Badge>
      )}
    </Link>
  );
}

export default AgentSettingsLink;
